import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveBackendMessage } from '../Utils/apiMessages';

export const useErrorHandler = () => {
  const { t } = useTranslation();

  /**
   * Process field-level validation errors and display them inline in forms
   * @param {Object} errorsObject - Object with field names as keys and error arrays as values
   * @param {Object} form - Ant Design form instance (optional)
   * @returns {boolean} - True if errors were handled
   */
  const handleFieldErrors = useCallback((errorsObject, form, fieldMap = {}) => {
    if (!errorsObject || typeof errorsObject !== 'object') return false;

    const fieldErrors = Object.entries(errorsObject).map(([fieldName, errorArray]) => {
      const backendErrors = Array.isArray(errorArray) ? errorArray : [errorArray];
      const mappedName = typeof fieldMap[fieldName] === 'function'
        ? fieldMap[fieldName](fieldName)
        : fieldMap[fieldName] || fieldName;

      // Process all errors for the field (may be multiple)
      const errorMessages = backendErrors.map(error => {
        const errorPayload = typeof error === 'string' ? { message: error } : (error || {});
        return resolveBackendMessage(t, {
          message_code: errorPayload.message_code,
          message: errorPayload.message,
          message_data: errorPayload.message_data
        });
      });

      return {
        name: mappedName,
        errors: errorMessages
      };
    });

    if (form) {
      // Set inline validation errors on the form
      // For nested keys like 'filters.knowledge_base', convert to array path
      const formFields = fieldErrors.map(({ name, errors }) => ({
        name: Array.isArray(name) ? name : (name.includes('.') ? name.split('.') : name),
        errors
      }));
      const existingFormFields = typeof form.getFieldsError === 'function'
        ? form.getFieldsError().map(({ name }) => ({
          name,
          errors: []
        }))
        : [];

      try {
        form.setFields([...existingFormFields, ...formFields]);
        if (typeof form.scrollToField === 'function' && formFields.length > 0) {
          form.scrollToField(formFields[0].name, { block: 'center' });
        }
        return true;
      } catch (error) {
        console.warn('Could not set form fields:', error);
        return false;
      }
    }

    return false;
  }, [t]);

  const showResponseMessage = useCallback(({
    messageCode,
    errorMessage,
    messageData,
    metadata,
  }) => {
    if (messageCode) {
      const resolved = resolveBackendMessage(t, {
        message_code: messageCode,
        message: errorMessage,
        message_data: messageData || metadata,
      });

      if (resolved) {
        message.error(resolved);
        return true;
      }
    }

    if (errorMessage) {
      message.error(errorMessage);
      return true;
    }

    return false;
  }, [t]);

  const handleError = useCallback((error, form = null, options = {}) => {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error details:', error);
    }

    const responseData = error.response?.data;

    // Handle API errors with structured payload
    if (responseData) {
      const {
        errors,
        message: errorMessage,
        message_code: messageCode,
        message_data: messageData,
        metadata,
      } = responseData;
      const fieldMap = options.fieldMap || {};

      // NEW FORMAT: Field-level validation errors (object with field keys)
      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const handled = handleFieldErrors(errors, form, fieldMap);
        const showedMessage = showResponseMessage({
          messageCode,
          errorMessage,
          messageData,
          metadata,
        });

        if (handled) {
          return;
        }

        if (showedMessage) {
          return;
        }
      }

      // OLD FORMAT: Field-level validation errors (array with field/description)
      if (Array.isArray(errors) && errors.length) {
        const handled = form ? handleFieldErrors(
          errors.reduce((acc, { field, description, message: fieldMessage, ...rest }) => {
            if (!field) return acc;
            acc[field] = [{ message: description || fieldMessage, ...rest }];
            return acc;
          }, {}),
          form,
          fieldMap,
        ) : false;

        const showedMessage = showResponseMessage({
          messageCode,
          errorMessage,
          messageData,
          metadata,
        });

        if (handled || showedMessage) {
          return;
        }

        errors.forEach(({ field, description }) => {
          message.error(
            t('field_error', {
              field,
              description,
              defaultValue: `${field}: ${description}`,
            }),
          );
        });
        return;
      }

      // Prefer localized message_code + dynamic parameters when available
      if (showResponseMessage({
        messageCode,
        errorMessage,
        messageData,
        metadata,
      })) {
        return;
      }
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      message.error(
        t('network_error_message', {
          defaultValue: 'Network error. Please check your connection and try again.',
        }),
      );
      return;
    }

    // Handle unexpected errors
    if (!responseData) {
      message.error(
        t('unexpected_error_occurred', {
          defaultValue: 'An unexpected error occurred',
        }),
      );
    }
  }, [t, handleFieldErrors, showResponseMessage]);

  return { handleError, handleErrorForm: handleError };
};
